import { type FC, useState, type ComponentProps, useReducer, useEffect } from "react";
import {
  type selectedItemProps,
  type ActionProps,
  formStateReducer,
  initialState,
} from "./modules";
import { Presentation, type ItemProps } from "./Presentation";

type Props = {
  categoryName: string;
  formName: string;
  itemsArray: ItemProps[];
  isBiyo?: boolean;
};

export type formStateContextProps = {
  formState: selectedItemProps;
  dispatchFormState: React.Dispatch<ActionProps>;
};

export const MultiSelectModal: FC<Props> = ({ categoryName, formName, itemsArray, isBiyo }) => {
  // モーダル開閉
  const [isShow, setIsShow] = useState(false);
  // 選択した条件を管理するstate
  const [formState, dispatchFormState] = useReducer(formStateReducer, initialState);
  // モーダル内のチェック状態を保持
  const [selectedValues, setSelectedValues] = useState<selectedItemProps>({});

  // 描画時のチェック状態をセット
  useEffect(() => {
    itemsArray.forEach((item) => {
      if (item.initialChecked) {
        dispatchFormState({
          type: "selectValue",
          value: item.value,
          name: item.text,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = (value: string, name: string) => {
    setSelectedValues((prevValues) => ({
      ...prevValues,
      [value]: { name },
    }));
  };

  const removeItem = (value: string) => {
    setSelectedValues((prevValues) => {
      const updatedValues = { ...prevValues };
      delete updatedValues[value];
      return updatedValues;
    });
  };

  const items: (ItemProps & ComponentProps<"input">)[] = itemsArray.map((item) => ({
    ...item,
    defaultChecked: Object.hasOwnProperty.call(formState, item.value),
    onChange: (event) => {
      // チェックボックスの状態が変更された時の処理
      if (event.target.checked) {
        addItem(event.target.value, event.target.name);
      } else {
        removeItem(event.target.value);
      }
    },
    name: item.text,
  }));

  // モーダル内の条件追加ボタン
  const handleOnClick = () => {
    const formStateValues = Object.keys(formState);
    const currentSelectValues = Object.keys(selectedValues);
    // 追加選択
    const willAddedValues = currentSelectValues.filter((i) => !formStateValues.includes(i));
    // 選択解除
    const willDeletedValues = formStateValues.filter((i) => !currentSelectValues.includes(i));

    willAddedValues.forEach((value) => {
      dispatchFormState({
        type: "selectValue",
        value,
        name: selectedValues[value].name,
      });
    });

    willDeletedValues.forEach((value) => {
      dispatchFormState({
        type: "unSelectValue",
        value,
      });
    });
    setIsShow(false);
  };

  useEffect(() => {
    setSelectedValues(formState);
  }, [formState]);

  return (
    <Presentation
      categoryName={categoryName}
      formName={formName}
      formState={formState}
      dispatchFormState={dispatchFormState}
      setIsShow={setIsShow}
      isShow={isShow}
      items={items}
      handleOnClick={handleOnClick}
      isBiyo={isBiyo}
    />
  );
};
